import axios from "axios";
import APP_VERSION from "@/config/version";
import { FEEDBACK_LABEL } from "@/server/types/feedback";
import { FEEDBACK_URL } from "./endpoint";
import {
  collectContext,
  resolveUsername,
  type RuntimeContext,
} from "./context";
import { getBreadcrumbs, type Breadcrumb } from "./breadcrumbs";
import { scrubText, scrubCode } from "./scrub";
import type { ErrorBoundaryScope } from "./ErrorBoundary";

const REPORT_PREFIX = "[AutoErrorReport]";

/** Where an error entered the pipeline. */
export type ErrorSource =
  | "boundary"
  | "window.onerror"
  | "unhandledrejection"
  | "manual"
  // Non-fatal: a known-transient error a wrapper handled instead of letting it
  // reach the boundary (e.g. the WebKit IndexedDB cursor bug caught by
  // useSafeLiveQuery). Tagged distinctly so these are filterable from crashes.
  | "suppressed"
  // IndexedDB open-path outcome (recovered-on-retry / recovered-on-reset /
  // terminal). Emitted by the hardened open in indexedDB/dbOpen.ts so we can
  // see which WebKit open-failure flavour fires in prod. Filterable from crashes.
  | "idb-open"
  // A `window.onerror` event whose `error` object AND `filename` are BOTH
  // withheld by the browser — the standard signature of a script from ANOTHER
  // ORIGIN throwing (browser extension, Cloudflare Turnstile, Jitsi). These
  // carry no actionable detail and, if built into a fabricated Error, their
  // stack would point at our own handler rather than the real failure site.
  // Emitted by globalHandlers.ts so they are filterable from real crashes.
  | "opaque-cross-origin"
  // Non-fatal: the DOM guard (domGuard.ts) intercepted a removeChild/insertBefore
  // against a node whose parent had changed — almost always a browser page
  // translator reparenting text behind React. Recovered instead of crashing the
  // route. Filterable so these never hide a genuine React bug.
  | "dom-guard";

export type ReportContentInput = {
  error: Error;
  componentStack?: string | null;
  eventId: string;
  scope: ErrorBoundaryScope;
  source?: ErrorSource;
  userMessage?: string;
};

export type ErrorReportPayload = ReportContentInput & {
  username: string;
};

/** A single link in an error's `cause` chain (scrubbed). */
export type SerializedErrorCause = {
  errorName: string;
  errorMessage: string;
  stack?: string;
};

export type SerializedErrorReport = {
  message: string;
  errorName: string;
  errorMessage: string;
  stack?: string;
  /**
   * The original error(s) attached via the ES2022 `{ cause }` option, walked
   * outermost→innermost. Without this, wrappers that rethrow a generic message
   * (e.g. `throw new Error("Error sending message", { cause })`) hide the real
   * failure from telemetry. Scrubbed like every other free-text field.
   */
  causeChain?: SerializedErrorCause[];
  componentStack?: string;
  eventId: string;
  scope: ErrorBoundaryScope;
  source: ErrorSource;
  route: string;
  userAgent: string;
  appVersion: string;
  timestamp: string;
  context: RuntimeContext;
  breadcrumbs: Breadcrumb[];
};

/** Max links to walk so a self-referential `cause` chain can't loop forever. */
const MAX_CAUSE_DEPTH = 5;

/**
 * Walks `error.cause` (outermost→innermost), scrubbing each link. Returns
 * undefined when there is no cause so the field is omitted from the report.
 */
function serializeCauseChain(
  error: unknown,
): SerializedErrorCause[] | undefined {
  const chain: SerializedErrorCause[] = [];
  const seen = new Set<unknown>();
  let current: unknown = (error as { cause?: unknown })?.cause;

  while (current != null && chain.length < MAX_CAUSE_DEPTH) {
    if (seen.has(current)) {
      break; // circular cause chain
    }
    seen.add(current);

    if (current instanceof Error) {
      chain.push({
        // `Error.name` is mutable free text, so scrub it like every other field.
        errorName: scrubText(current.name),
        errorMessage: scrubText(current.message),
        stack: scrubCode(current.stack),
      });
      current = (current as { cause?: unknown }).cause;
    } else {
      // Non-Error cause (string, object, etc.) — capture a scrubbed string.
      chain.push({
        errorName: typeof current,
        errorMessage: scrubText(String(current)),
      });
      break;
    }
  }

  return chain.length > 0 ? chain : undefined;
}

function scrubBreadcrumb(crumb: Breadcrumb): Breadcrumb {
  return {
    ...crumb,
    message: scrubText(crumb.message),
    ...(crumb.data
      ? {
          data: Object.fromEntries(
            Object.entries(crumb.data).map(([k, v]) => [
              k,
              typeof v === "string" ? scrubText(v) : v,
            ]),
          ),
        }
      : {}),
  };
}

export function buildReportContent(
  payload: ReportContentInput,
): SerializedErrorReport {
  const { error, componentStack, eventId, scope, userMessage } = payload;
  const scrubbedMessage = scrubText(error.message);
  const note = userMessage?.trim() ? scrubText(userMessage.trim()) : "";
  const userPart = note ? `\n\nUser note: ${note}` : "";
  return {
    message: `${REPORT_PREFIX} ${scrubbedMessage || error.name}${userPart}`,
    errorName: error.name,
    errorMessage: scrubbedMessage,
    stack: scrubCode(error.stack),
    causeChain: serializeCauseChain(error),
    componentStack: scrubCode(componentStack),
    eventId,
    scope,
    source: payload.source ?? "boundary",
    route: typeof window !== "undefined" ? window.location.pathname : "",
    userAgent:
      typeof navigator !== "undefined" ? navigator.userAgent : "unknown",
    appVersion: APP_VERSION,
    timestamp: new Date().toISOString(),
    context: collectContext(),
    breadcrumbs: getBreadcrumbs().map(scrubBreadcrumb),
  };
}

/**
 * Wraps a serialized report in the `{ label, content, username }` envelope the
 * Rust feedback backend expects. Content is double-encoded as
 * `{ image, message }` to match the existing ReportContent shape.
 */
export function buildFeedbackBody(
  content: SerializedErrorReport,
  username: string,
): Record<string, unknown> {
  const resolvedUsername = username || resolveUsername() || "anonymous";
  return {
    label: FEEDBACK_LABEL.BUG_REPORT,
    content: JSON.stringify({
      image: "",
      message: JSON.stringify(content),
    }),
    username: resolvedUsername,
  };
}

export async function reportError(payload: ErrorReportPayload): Promise<void> {
  // reportError is the manual ("Report this error") path; tag it as such unless
  // the caller overrides, so user-submitted reports are distinguishable from
  // automatic boundary/global captures.
  const content = buildReportContent({
    ...payload,
    source: payload.source ?? "manual",
  });
  const body = buildFeedbackBody(content, payload.username);

  // POST directly — bypasses the authenticated axios client so reports
  // work even when the auth state is broken (the exact scenario that
  // triggers the error boundary). This path is awaited so the manual
  // "Report" UI can surface success/failure to the user.
  await axios.post(FEEDBACK_URL, body, { timeout: 15000 });
}
